import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isOrganizationAdmin } from '../utils/planPermissions';

const PAYMENT_FAILURE_STORAGE_KEY = 'payment_failure_notification_shown';
const PAYMENT_FAILURE_CACHE_KEY = 'payment_failure_cache';

const usePaymentNotifications = (user) => {
  const [paymentData, setPaymentData] = useState(null);
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasPaymentFailure, setHasPaymentFailure] = useState(false);
  const [cachedPaymentState, setCachedPaymentState] = useState(null);

  // Check if user is admin or owner
  const isAdminOrOwner = isOrganizationAdmin();

  // Initialize cached payment state from localStorage
  useEffect(() => {
    const getCachedPaymentState = () => {
      try {
        const cached = localStorage.getItem(PAYMENT_FAILURE_CACHE_KEY);
        if (cached) {
          const parsedCache = JSON.parse(cached);
          // Check if cache is still valid (less than 5 minutes old)
          const now = new Date();
          const cacheTime = new Date(parsedCache.timestamp);
          const minutesSinceCache = (now - cacheTime) / (1000 * 60);
          
          // Cache is valid for 2 minutes (server-side catches real-time issues)
          if (minutesSinceCache < 2) {
            return parsedCache;
          }
        }
      } catch (error) {
        console.error('Error reading cached payment state:', error);
        localStorage.removeItem(PAYMENT_FAILURE_CACHE_KEY);
      }
      return null;
    };

    const cached = getCachedPaymentState();
    if (cached) {
      setCachedPaymentState(cached);
      setHasPaymentFailure(cached.hasPaymentFailure);
      if (cached.hasPaymentFailure) {
        setPaymentData(cached.paymentData);
        setShowPaymentWarning(true);
      }
    }
  }, []);

  // Cache payment state to localStorage
  const updatePaymentCache = useCallback((hasFailure, data) => {
    try {
      const cacheData = {
        hasPaymentFailure: hasFailure,
        paymentData: data,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(PAYMENT_FAILURE_CACHE_KEY, JSON.stringify(cacheData));
      setCachedPaymentState(cacheData);
    } catch (error) {
      console.error('Error caching payment state:', error);
    }
  }, []);

  // Fetch payment failure status
  const fetchPaymentStatus = useCallback(async (forceRefresh = false) => {
    // Use cache if available and not forcing refresh
    if (!forceRefresh && cachedPaymentState && !isLoading) {
      const now = new Date();
      const cacheTime = new Date(cachedPaymentState.timestamp);
      const minutesSinceCache = (now - cacheTime) / (1000 * 60);
      
      if (minutesSinceCache < 2) {
        return;
      }
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/payment-failure-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const data = response.data.data;
        
        // Handle payment failure scenario
        if (data.hasPaymentFailure) {
          setHasPaymentFailure(true);
          setPaymentData(data);
          setShowPaymentWarning(true);
          updatePaymentCache(true, data);
        } else {
          setHasPaymentFailure(false);
          setPaymentData(null);
          setShowPaymentWarning(false);
          updatePaymentCache(false, null);
          
          // Clear any stored dismissal state if payment is now working
          localStorage.removeItem(PAYMENT_FAILURE_STORAGE_KEY);
        }
      } else {
        console.error('Payment status check failed:', response.data);
      }
    } catch (err) {
      console.error('Error fetching payment status:', err);
      setError(err.response?.data?.message || 'Failed to fetch payment status');
    } finally {
      setIsLoading(false);
    }
  }, [cachedPaymentState, isLoading, updatePaymentCache]);

  // Dismiss payment warning (temporary dismissal)
  const dismissPaymentWarning = useCallback(() => {
    if (!paymentData) return;

    try {
      // Store dismissal in localStorage with timestamp
      const dismissalData = {
        timestamp: new Date().toISOString(),
        subscriptionStatus: paymentData.subscriptionStatus
      };
      localStorage.setItem(PAYMENT_FAILURE_STORAGE_KEY, JSON.stringify(dismissalData));
      
      setShowPaymentWarning(false);
    } catch (error) {
      console.error('Error dismissing payment warning:', error);
    }
  }, [paymentData]);

  // Check if payment warning should be shown (not dismissed recently)
  const shouldShowPaymentWarning = useCallback(() => {
    if (!hasPaymentFailure) return false;

    // Inactive subscription (canceled, etc.) — always show blocking banner like usage limit
    if (paymentData?.isSubscriptionInactive) {
      return true;
    }
    
    try {
      const dismissalData = localStorage.getItem(PAYMENT_FAILURE_STORAGE_KEY);
      if (dismissalData) {
        const parsed = JSON.parse(dismissalData);
        const dismissalTime = new Date(parsed.timestamp);
        const now = new Date();
        const hoursSinceDismissal = (now - dismissalTime) / (1000 * 60 * 60);
        
        // Show warning again after 24 hours or if subscription status changed
        if (hoursSinceDismissal < 24 && parsed.subscriptionStatus === paymentData?.subscriptionStatus) {
          return false;
        }
      }
    } catch (error) {
      console.error('Error checking dismissal state:', error);
    }
    
    return true;
  }, [hasPaymentFailure, paymentData]);

  // Update show warning state based on dismissal logic
  useEffect(() => {
    if (hasPaymentFailure && paymentData) {
      const shouldShow = shouldShowPaymentWarning();
      setShowPaymentWarning(shouldShow);
    }
  }, [hasPaymentFailure, paymentData, shouldShowPaymentWarning]);

  // Initial fetch on mount
  useEffect(() => {
    if (user) {
      fetchPaymentStatus();
    }
  }, [user, fetchPaymentStatus]);

  // Poll for updates every 5 minutes (server-side protection catches real-time issues)
  useEffect(() => {
    const pollInterval = 5 * 60 * 1000; // 5 minutes
    
    const interval = setInterval(() => {
      fetchPaymentStatus();
    }, pollInterval);
    
    return () => clearInterval(interval);
  }, [fetchPaymentStatus]);

  return {
    paymentData,
    showPaymentWarning,
    hasPaymentFailure,
    isLoading,
    error,
    isAdminOrOwner,
    dismissPaymentWarning,
    refreshPaymentStatus: () => fetchPaymentStatus(true)
  };
};

export default usePaymentNotifications; 